import React, { createContext, useContext, useEffect, useState } from 'react'
import { authService, AuthUser } from '../services/auth'
import { checkOnboardingStatus } from '../services/userProfile'
import toast from 'react-hot-toast'

interface AuthContextType {
  user: AuthUser | null
  loading: boolean
  onboardingCompleted: boolean
  onboardingLoading: boolean
  signUp: (email: string, password: string, displayName?: string) => Promise<void>
  signIn: (email: string, password: string) => Promise<void>
  signInWithGoogle: () => Promise<void>
  signOut: () => Promise<void>
  resetPassword: (email: string) => Promise<void>
  refreshOnboardingStatus: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [onboardingCompleted, setOnboardingCompleted] = useState(false)
  const [onboardingLoading, setOnboardingLoading] = useState(false)

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged(async (user) => {
      setUser(user)

      // Check onboarding status if user is logged in
      if (user) {
        setOnboardingLoading(true)
        try {
          const completed = await checkOnboardingStatus(user.uid)
          setOnboardingCompleted(completed)
        } catch (error) {
          //console.error('Failed to check onboarding status:', error)
          // Default to false if we can't check (don't crash the app)
          setOnboardingCompleted(false)
        } finally {
          setOnboardingLoading(false)
        }
      } else {
        setOnboardingCompleted(false)
        setOnboardingLoading(false)
      }

      setLoading(false)
    })

    return unsubscribe
  }, [])

  const signUp = async (email: string, password: string, displayName?: string) => {
    try {
      const user = await authService.signUp(email, password, displayName)
      setUser(user)
      toast.success('Account created successfully!')
    } catch (error: any) {
      toast.error(error.message || 'Failed to create account')
      throw error
    }
  }

  const signIn = async (email: string, password: string) => {
    try {
      const user = await authService.signIn(email, password)
      setUser(user)
      toast.success('Signed in successfully!')
    } catch (error: any) {
      // Don't show toast for errors that will redirect to signup
      if (
        error?.code !== 'auth/user-not-found' &&
        error?.code !== 'auth/invalid-credential' &&
        error?.code !== 'auth/invalid-login-credentials'
      ) {
        toast.error(error.message || 'Failed to sign in')
      }
      throw error
    }
  }

  const signInWithGoogle = async () => {
    try {
      const user = await authService.signInWithGoogle()
      setUser(user)
      toast.success('Signed in with Google successfully!')
    } catch (error: any) {
      toast.error(error.message || 'Failed to sign in with Google')
      throw error
    }
  }

  const signOut = async () => {
    try {
      await authService.signOut()
      setUser(null)
      toast.success('Signed out successfully!')
    } catch (error: any) {
      toast.error(error.message || 'Failed to sign out')
      throw error
    }
  }

  const resetPassword = async (email: string) => {
    try {
      await authService.resetPassword(email)
      toast.success('Password reset email sent!')
    } catch (error: any) {
      toast.error(error.message || 'Failed to send reset email')
      throw error
    }
  }

  const refreshOnboardingStatus = async () => {
    if (user) {
      setOnboardingLoading(true)
      try {
        const completed = await checkOnboardingStatus(user.uid)
        setOnboardingCompleted(completed)
      } catch (error) {
        //console.error('Failed to refresh onboarding status:', error)
        // Don't change the current state if refresh fails
      } finally {
        setOnboardingLoading(false)
      }
    }
  }

  const value = {
    user,
    loading,
    onboardingCompleted,
    onboardingLoading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resetPassword,
    refreshOnboardingStatus,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
